import type { ProfileCR } from '@/api-client/api';
import { useProfileStore } from '@/stores/profiles/profiles';

export interface ProfileSelectOption {
  value: ProfileCR;
  icon: string;
  display: string;
  details: string;
  disabled: boolean;
}

/**
 * Returns a string summary for the given location
 * that is suitable for display in the UI.
 */
export function summarizeLocationProfile(profile: ProfileCR) {
  const location = profile?.spec?.locationSpec;
  if (location === undefined) return '';
  const provider = location?.objectStore?.objectStoreType;
  const region = location?.objectStore?.region;
  const bucket = location?.objectStore?.name;
  const vbrServerAddr = location?.vbr?.serverAddress;
  const vbrServerPort = location?.vbr?.serverPort;
  const vbrRepo = location?.vbr?.repoName;
  const items: string[] = [];
  if (provider) items.push(provider);
  if (region) items.push(region);
  if (bucket) items.push(`"${bucket}"`);
  if (vbrServerAddr) items.push(`${vbrServerAddr}:${vbrServerPort}`);
  if (vbrRepo) items.push(`"${vbrRepo}"`);

  return items.join(', ');
}

/**
 * Fetch profiles and return array of <k-select> option objects
 */
export function createLocationProfileSelectorOptions({
  profiles,
  disableVBR = false,
}: {
  profiles: ProfileCR[];
  disableVBR?: boolean;
}) {
  return profiles?.reduce((acc: ProfileSelectOption[], profile) => {
    let details = summarizeLocationProfile(profile);
    let disabled = false;

    if (profile.status?.validation === 'Failed') {
      disabled = true;
      details += `<br/><span class="text-danger">Failed validation.</span> See settings for details.`;
    }

    if (!!profile.spec?.locationSpec?.vbr && disableVBR) {
      disabled = true;
      details += `<br>VBR profiles not permitted here`;
    }

    acc.push({
      value: profile,
      icon: 'icon-cloud-location-alt',
      display: profile.metadata?.name ?? '',
      details,
      disabled,
    });

    return acc;
  }, []);
}

export function getLocationProfileByName(name: string) {
  const profileStore = useProfileStore();
  return profileStore.profilesLocation?.find((p) => p?.metadata?.name === name);
}
export function getGlobalLocationProfileByName(name: string) {
  const profileStore = useProfileStore();
  return profileStore.profilesGlobalLocation?.find((p) => p?.metadata?.name === name);
}

/**
 * Returns the names of profiles that have immutability enabled
 */
export function getImmutableProfileNames() {
  const profileStore = useProfileStore();
  const names = profileStore.profilesLocation?.reduce((acc: string[], p) => {
    if (!p.spec?.locationSpec?.objectStore?.protectionPeriod) return acc;
    if (p.metadata?.name) acc.push(p.metadata.name);
    return acc;
  }, []);
  return names ?? [];
}

export function isProfileVBR(profile: ProfileCR) {
  let locationSpecType = profile?.spec?.locationSpec?.type;
  if (!locationSpecType && profile.metadata?.name) {
    const fullProfile = getLocationProfileByName(profile.metadata.name);
    locationSpecType = fullProfile?.spec?.locationSpec?.type;
  }

  return locationSpecType === 'VBR';
}
